import getConfig from 'next/config'
import { getCookieValue } from '@fe-toolkit/utility/node'
import { headerFlagKeys } from '@giffgaff/fe-design-system-content'

import { HEADER_FLAGS_OFF } from './HeaderFlagsContext'

import type { ServerResponse } from 'http'
import type { NextPageContext } from 'next'
import type { Feature } from '@fe-toolkit/utility/client/tracking-context'
import type { HeaderFlags } from './HeaderFlagsContext'

const { serverRuntimeConfig } = getConfig()

const AUTH_COOKIE = '__s_idk__'

// Same budget as fe-common's resolveChromeFlags (800 ms)
// https://github.com/giffgaff/fe-common/blob/main/src/api/client-tracking-context/resolveChromeFlags.ts
const CTC_TIMEOUT_MS = 800

const isEnabled = (features: Feature[], name: string) =>
  features.some(
    feature =>
      feature.name === name &&
      (feature.value === true || feature.value === 'true'),
  )

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`CTC lookup timed out after ${ms} ms`)),
      ms,
    )
  })

  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer))
}

const appendSetCookie = (res: ServerResponse, cookie: string) => {
  const existing = res.getHeader('Set-Cookie') ?? []
  res.setHeader('Set-Cookie', [
    ...(Array.isArray(existing) ? existing : [String(existing)]),
    cookie,
  ])
}

/**
 * Resolves the Eppo flags for fe-common's CommonHeader and CommonFooter.
 *
 * Default behavior is to return all header flags as off.
 */
export async function getHeaderFlags(
  ctx: NextPageContext,
): Promise<HeaderFlags> {
  // ctx.req is only set while SSR, not on browser navigation or the build-time.
  if (!ctx.req) return HEADER_FLAGS_OFF

  // Captures ctc-session so it is appended to, not replaces, the response's Set-Cookie.
  let ctcSessionCookie: string | undefined
  const capturingCtx = {
    ...ctx,
    res: {
      setHeader: (_name: string, value: string) => {
        ctcSessionCookie = value
      },
    },
  } as unknown as NextPageContext

  try {
    // getHeaderFlags is called in getInitialProps, which also ships to the browser too.
    // A dynamic import keeps the CTC client (about 22 kB) out of every page's bundle.
    const { getServerSideFeaturesNJS } =
      await import('@fe-toolkit/utility/client/tracking-context')
    const features = await withTimeout(
      getServerSideFeaturesNJS({
        // fe-common reads the same keys from feds-content, so the names cannot drift apart.
        requestedFeatures: Object.values(headerFlagKeys),
        apiGatewayUrl: serverRuntimeConfig.INTERNAL_API_GATEWAY_URL,
        nextPageContext: capturingCtx,
        // Without these, flags cached on the ctc-session cookie survive login,
        // logout and account switches, so member-targeted values go stale.
        // An empty auth cookie counts as logged out.
        isAuthenticated: context =>
          Boolean(getCookieValue(context.req?.headers.cookie, AUTH_COOKIE)),
        authIdentity: context =>
          getCookieValue(context.req?.headers.cookie, AUTH_COOKIE),
      }),
      CTC_TIMEOUT_MS,
    )

    if (ctx.res && ctcSessionCookie) appendSetCookie(ctx.res, ctcSessionCookie)

    return {
      isBrandRefresh: isEnabled(features, headerFlagKeys.brandRefresh),
      isBlackFriday: isEnabled(features, headerFlagKeys.blackFriday),
      isBlackFridayPrereg: isEnabled(
        features,
        headerFlagKeys.blackFridayPrereg,
      ),
    }
  } catch (err: unknown) {
    console.info(`getHeaderFlags throws Error: ${String(err)}`)
    return HEADER_FLAGS_OFF
  }
}
